import React, { FC, useEffect, useState, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { getProvidersListFetch, getWaitingProvidersListFetch } from 'src/resources/providersList/reducer';
import { RootStoreType } from 'src/types/store';
import { allColumns, waitingColumns } from './constants';
import StoreIcon from '@mui/icons-material/Store';
import TableSimple from 'src/components/TableSimple';
import { ExportTypeType, TableRefType, SelectRowType, TableRowType, CustomRenderType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import { SettingsIcon, TransferArrowsIcon } from 'src/components/SkladonIcon/SmallIcons';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import { getProviderStatusCellStyle, getProviderStatusOptionStyle } from 'src/utils/providerStatusColorMap';

const ProvidersListContainer: FC = (props) => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>();
    const [activeTab, setActiveTab] = useState<string>('providers');
    const [selectedRows, setSelectedRows] = useState<SelectRowType[]>([]);

    const loading = useSelector((state: RootStoreType) => state.providersList.loading);
    const providersData = useSelector((state: RootStoreType) => state.providersList.data);
    const providersWaitingData = useSelector((state: RootStoreType) => state.providersList.dataWaiting);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const clientTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.clientTypeCodes);
    const businessFieldCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.businessFieldCodes);
    const contractTermCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.contractTermCodes);
    const salesRepresentativeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.salesRepresentativeCodes);
    const keyAccountManagerCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.keyAccountManagerCodes);
    const providerStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.providerStatusCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);

    const permissionCreate = useCheckPermission('PROVIDER.CREATE');
    const permissionDetail = useCheckPermission('PROVIDER.DETAIL');
    const permissionUpdate = useCheckPermission('PROVIDER.UPDATE');
    const permissionConfigDetail = useCheckPermission('PROVIDER_CONFIG.DETAIL');
    const permissionIntegrationConfigDetail = useCheckPermission('PROVIDER_INTEGRATION_CONFIG.DETAIL');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('providers.title') }));
        // Load all providers including inactive ones
        dispatch(getProvidersListFetch({ forceAll: true }));
        dispatch(getWaitingProvidersListFetch());
    }, []);

    // Clear selection when switching tabs
    useEffect(() => {
        setSelectedRows([]);
    }, [activeTab]);

    const reloadProvidersList = () => {
        dispatch(activeTab === 'providers' ? getProvidersListFetch({ forceAll: true }) : getProvidersListFetch({ forceAll: true }));
        setSelectedRows([]); // Clear selection when reloading
    };

    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTable(type, translate('providers.action.exportFileName'));
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const customRender: CustomRenderType = (column, row) => {
        if (column.key === 'STATUS' && (row as any).STATUS) {
            const status = (row as any).STATUS;
            return (
                <span style={getProviderStatusCellStyle(status)}>
                    {providerStatusCodes?.find((code) => code.value === status)?.label || status}
                </span>
            );
        }
        if (column.key === 'BILLING_EMAIL' && (row as any).BILLING_EMAIL) {
            const billingEmail = (row as any).BILLING_EMAIL;
            const emails = billingEmail.split(',').map((email: string) => email.trim());
            return (
                <>
                    {emails.map((email: string, index: number) => (
                        <div key={index}>{email}</div>
                    ))}
                </>
            );
        }
        return null;
    };

    return (
        <>
            <TopBlock
                reloadProvidersList={reloadProvidersList}
                permissionCreate={permissionCreate}
                permissionUpdate={permissionUpdate}
                handleExport={handleExport}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedRows={selectedRows}
            />
            <TableSimple
                tableRef={tableRef}
                rowIdKey="ID"
                rows={(activeTab === 'providers' ? providersData : providersWaitingData) || []}
                loading={loading}
                columsLabelPrefix="providers.table"
                allColumns={activeTab === 'providers' ? allColumns : waitingColumns}
                initSort={{ column: 'SKU', direction: 'DESC' }}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                rowActions={[
                    {
                        icon: <StoreIcon />,
                        label: translate('providers.action.providerDetail'),
                        getLink: (row) => `/admin/providers/${row.ID}/`,
                        hiddenFce: () => !permissionDetail,
                    },
                    {
                        icon: <SettingsIcon />,
                        label: translate('providers.action.providerConfig'),
                        getLink: (row) => `/admin/providers/config/${row.ID}/`,
                        hiddenFce: () => !permissionConfigDetail || activeTab !== 'providers',
                    },
                    {
                        icon: <TransferArrowsIcon />,
                        label: translate('providers.action.providerIntegrationConfig'),
                        getLink: (row) => `/admin/providers/integration-config/${row.ID}/`,
                        hiddenFce: (row) => !permissionIntegrationConfigDetail || activeTab !== 'providers',
                    },
                ]}
                selectOptions={{
                    boolNumberCodes,
                    clientTypeCodes,
                    businessFieldCodes,
                    contractTermCodes,
                    salesRepresentativeCodes,
                    keyAccountManagerCodes,
                    providerStatusCodes,
                }}
                customRender={customRender}
                optionStyleFce={getProviderStatusOptionStyle}
                heightVariant="top-tab-2"
                fullHeight
                optionsSessionName="providersTable"
                userSettingsName="showProvidersTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
            />
        </>
    );
};

export default ProvidersListContainer;
